import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, X } from "lucide-react";
import { showExpertRequestsEntry } from "@/lib/platform-counts";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { ER_COPY } from "@/lib/expert-requests-copy";

const pendingKey = (uid: string) => `td_org_chat_pending_${uid}`;
const shownKey = (uid: string) => `td_org_chat_shown_${uid}`;

/** После добавления события: показать приглашение в чат организаторов (один раз). */
export function markOrganizerChatInvitePending(uid: string | undefined) {
  if (!uid) return;
  try {
    if (localStorage.getItem(shownKey(uid)) !== "1") localStorage.setItem(pendingKey(uid), "1");
  } catch {
    /* ignore */
  }
}

/** Карточка «Ты организатор. Напиши Дарье…». Показывается один раз для человека. */
export function OrganizerChatInvite({ className = "" }: { className?: string }) {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!user) return;
    try {
      if (
        localStorage.getItem(pendingKey(user.id)) === "1" &&
        localStorage.getItem(shownKey(user.id)) !== "1"
      ) {
        setShow(true);
        localStorage.setItem(shownKey(user.id), "1");
        localStorage.removeItem(pendingKey(user.id));
      }
    } catch {
      /* ignore */
    }
  }, [user]);
  if (!show) return null;
  return (
    <div
      className={`relative rounded-2xl border border-td-line bg-td-soft/60 p-4 pr-11 ${className}`}
    >
      <p className="text-sm text-td-dark">{ER_COPY.chatInviteText}</p>
      <a
        href={ER_COPY.chatInviteUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="cta-solid mt-3 inline-flex min-h-10 items-center rounded-full px-5 text-sm font-semibold"
      >
        {ER_COPY.chatInviteButton}
      </a>
      <button
        type="button"
        aria-label="Закрыть"
        onClick={() => setShow(false)}
        className="absolute right-1 top-1 flex size-10 items-center justify-center rounded-full text-td-muted hover:bg-td-soft"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}

/** Тонкая строка «Запросы экспертов» во вкладке «Мои» афиши для организаторов. */
export function ExpertRequestsPlate({ className = "" }: { className?: string }) {
  const { user } = useAuth();
  const uid = user?.id;
  const { data: isOrganizer } = useQuery({
    queryKey: ["is-organizer-for-experts", uid],
    enabled: !!uid,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const [{ data: p }, { count }] = await Promise.all([
        supabase.from("profiles").select("hosts_for_experts").eq("user_id", uid!).maybeSingle(),
        supabase
          .from("events")
          .select("id", { count: "exact", head: true })
          .eq("organizer_user_id", uid!),
      ]);
      return showExpertRequestsEntry({
        hostsForExperts: p?.hosts_for_experts,
        ownEventsCount: count,
      });
    },
  });
  if (!isOrganizer) return null;
  return (
    <Link
      to="/expert-requests"
      search={{ tab: "requests" }}
      className={`flex min-h-11 min-w-0 items-center justify-between gap-3 rounded-xl border border-td-line bg-white px-4 py-2 text-sm hover:bg-td-soft/40 ${className}`}
    >
      <span className="min-w-0 truncate text-td-dark">
        <span className="font-semibold">{ER_COPY.eventsPlate}:</span>{" "}
        <span className="text-td-muted">эксперты ищут площадки</span>
      </span>
      <ChevronRight className="size-4 shrink-0 text-td-teal" aria-hidden />
    </Link>
  );
}
